import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AdminAdmTransferService } from "../../../../api/services/admin-adm-transfer.service";
import { AdmTransferDocument } from "../../../../api/models/adm-transfer-document";
import { AdmTransferPlan } from "../../../../api/models/adm-transfer-plan";
import { AdmTransferItem } from "../../../../api/models/adm-transfer-item";
import { AdmTransferApplyResult } from "../../../../api/models/adm-transfer-apply-result";

// The item types a plan can contain, in the order they are shown (and the label for each).
export const TRANSFER_TYPES = [
  { value: "Table", label: "Tables" },
  { value: "FieldGroup", label: "Field groups" },
  { value: "Column", label: "Columns" },
  { value: "FlattenedColumn", label: "Flattened columns" },
  { value: "Relationship", label: "Relationships" },
  { value: "FilterPoint", label: "Filter points" },
  { value: "Group", label: "Groups" },
];

const CONFIRM_WORD = "IMPORT";

type ActionFilter = "changes" | "all" | "guarded" | "blocked";

// Preview, confirm and apply an import document. Used by the Import screen (a file) and by "Reuse flattened
// columns" (a document built from a template table): the document is previewed against the current definition
// (nothing is written), every guarded overwrite must be acknowledged, and the apply is one all-or-nothing
// transaction that is refused if the plan changed since the preview.
@Component({
  selector: "admin-adm-transfer-review",
  templateUrl: "./admin-adm-transfer-review.component.html",
})
export class AdminAdmTransferReviewComponent implements OnInit {
  @Input() document: AdmTransferDocument;
  // Label of the step before "Preview" ("File" or "Select").
  @Input() firstStep = "File";
  @Input() finishLabel = "Done";
  @Output() back = new EventEmitter<void>();
  @Output() finished = new EventEmitter<void>();
  @Output() imported = new EventEmitter<void>();
  @Output() openValidation = new EventEmitter<void>();

  // 0 = preview, 1 = confirm, 2 = result
  public step = 0;
  public previewing = false;
  public plan: AdmTransferPlan | null = null;
  public previewError = "";
  public actionFilter: ActionFilter = "changes";
  public itemSearch = "";
  public expanded = new Set<string>();
  public acknowledged = new Set<string>();
  public typedConfirmation = "";
  public applying = false;
  public applyError = "";
  public result: AdmTransferApplyResult | null = null;

  public readonly types = TRANSFER_TYPES;
  public readonly confirmWord = CONFIRM_WORD;

  constructor(private transferService: AdminAdmTransferService) {}

  get stepNames(): string[] { return [this.firstStep, "Preview", "Confirm", "Result"]; }

  ngOnInit() {
    this.preview();
  }

  // ---- preview ----
  preview() {
    this.previewing = true;
    this.previewError = "";
    this.transferService.adminAdmTransferImportPreview({ document: this.document }).subscribe({
      next: (plan) => {
        this.previewing = false;
        this.plan = plan;
        this.step = 0;
        this.acknowledged.clear();
        this.typedConfirmation = "";
        this.expanded.clear();
        // Open straight onto what matters: anything blocked, else changes.
        this.actionFilter = plan.blocked > 0 ? "blocked" : "changes";
      },
      error: (e) => {
        this.previewing = false;
        this.previewError = this.errorText(e, "The preview failed");
      },
    });
  }

  get items(): AdmTransferItem[] { return this.plan?.items || []; }

  itemsOfType(type: string): AdmTransferItem[] {
    const q = this.itemSearch.trim().toLowerCase();
    return this.items.filter((i) => {
      if (i.type !== type) return false;
      if (q && !(i.key || "").toLowerCase().includes(q) && !(i.table || "").toLowerCase().includes(q)) return false;
      switch (this.actionFilter) {
        case "changes": return i.action !== "Unchanged";
        case "guarded": return i.action === "Update" && i.hasGuardedChanges;
        case "blocked": return i.action === "Blocked";
        default: return true;
      }
    });
  }

  countOfType(type: string, action?: string): number {
    return this.items.filter((i) => i.type === type && (!action || i.action === action)).length;
  }

  get visibleCount(): number { return this.types.reduce((n, t) => n + this.itemsOfType(t.value).length, 0); }

  toggleExpanded(item: AdmTransferItem) {
    const id = item.ackKey;
    this.expanded.has(id) ? this.expanded.delete(id) : this.expanded.add(id);
  }

  expandAll(open: boolean) {
    this.expanded.clear();
    if (open) this.types.forEach((t) => this.itemsOfType(t.value).forEach((i) => this.expanded.add(i.ackKey)));
  }

  badgeClass(action: string): string {
    switch (action) {
      case "Create": return "badge badge-success";
      case "Update": return "badge badge-primary";
      case "Blocked": return "badge badge-danger";
      default: return "badge badge-secondary";
    }
  }

  impactText(item: AdmTransferItem): string {
    const i = item.impact;
    if (!i) return "";
    const parts: string[] = [];
    if (i.layouts > 0) parts.push(`${i.layouts} ADM${i.layouts === 1 ? "" : "s"}`);
    if (i.groups > 0) parts.push(`${i.groups} Group${i.groups === 1 ? "" : "s"}`);
    if (i.flattenedColumns > 0) parts.push(`${i.flattenedColumns} flattened column${i.flattenedColumns === 1 ? "" : "s"}`);
    return parts.length > 0 ? `Used by ${parts.join(", ")}` : "";
  }

  downloadReport() {
    if (!this.plan) return;
    const blob = new Blob([JSON.stringify(this.plan, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `adm-import-preview-${new Date().toISOString().substring(0, 10)}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // ---- confirm ----
  get guardedItems(): AdmTransferItem[] { return this.items.filter((i) => i.action === "Update" && i.hasGuardedChanges); }
  get guardedInUse(): AdmTransferItem[] { return this.guardedItems.filter((i) => i.impact && (i.impact.layouts > 0 || i.impact.groups > 0 || i.impact.flattenedColumns > 0)); }
  get allAcknowledged(): boolean { return this.guardedItems.every((i) => this.acknowledged.has(i.ackKey)); }
  get needsTypedConfirmation(): boolean { return this.guardedInUse.length > 0; }
  get typedConfirmationOk(): boolean { return !this.needsTypedConfirmation || this.typedConfirmation.trim() === CONFIRM_WORD; }
  get canApply(): boolean { return !!this.plan && this.plan.canApply && this.allAcknowledged && this.typedConfirmationOk && !this.applying; }

  toggleAcknowledged(item: AdmTransferItem, on: boolean) { on ? this.acknowledged.add(item.ackKey) : this.acknowledged.delete(item.ackKey); }
  acknowledgeAll(on: boolean) {
    this.acknowledged.clear();
    if (on) this.guardedItems.forEach((i) => this.acknowledged.add(i.ackKey));
  }

  guardedChanges(item: AdmTransferItem) { return (item.changes || []).filter((c) => c.guarded); }

  goToConfirm() { this.step = 1; }

  // ---- apply ----
  apply() {
    if (!this.canApply || !this.plan) return;
    this.applying = true;
    this.applyError = "";
    this.transferService
      .adminAdmTransferImportApply({ document: this.document, planHash: this.plan.planHash, acknowledgedKeys: Array.from(this.acknowledged) })
      .subscribe({
        next: (result) => {
          this.applying = false;
          this.result = result;
          this.step = 2;
          this.imported.emit();
        },
        error: (e) => {
          this.applying = false;
          this.applyError = this.errorText(e, "The import failed. Nothing was changed.");
          // The definition moved on since the preview: send the admin back to a fresh preview.
          if (/Preview the import again/i.test(this.applyError)) this.preview();
        },
      });
  }

  get resultErrors() { return (this.result?.issues || []).filter((i) => i.severity === 1); }
  get resultWarnings() { return (this.result?.issues || []).filter((i) => i.severity === 0); }

  // ---- navigation ----
  back1() { if (this.step === 1) this.step = 0; }
  backToStart() { this.back.emit(); }

  private errorText(e: any, fallback: string): string {
    const body = e && e.error;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && (body.title || body.message)) return body.title || body.message;
    return fallback;
  }
}
